/**
 * Voice-output controls shared by Voice and Conversation modes.
 *
 * One row owns the whole "hear the translation" story: whether finished
 * utterances are read aloud, in which Piper voice gender, and what the
 * on-device engine is doing (downloading a ~60 MB model, speaking, blocked by
 * autoplay policy, or failed with the real reason shown instead of a shrug).
 */

import { useState } from "react";
import type { LanguageCode } from "@shared/types/language";
import { languageShortLabel } from "@shared/languages";
import { describePiperVoice, type PiperGender, type PiperState, type ResolvedPiperVoice } from "../lib/piper";
import { Badge, Button, Segmented } from "./ui";
import { IconDownload, IconFemale, IconMale, IconMute, IconPlay, IconSpeaker, IconStop } from "./icons";

interface Props {
  targetLanguage: LanguageCode;
  gender: PiperGender;
  onGenderChange: (gender: PiperGender) => void;
  speakOut: boolean;
  onSpeakOutChange: (value: boolean) => void;
  /** Live Piper engine state from the session. */
  piper: PiperState;
  /** The voice currently resolved for `targetLanguage`. */
  piperVoice: ResolvedPiperVoice;
  onEnsureVoice: () => void;
  onTestVoice: () => Promise<void>;
  onStopSpeech: () => void;
  speaking: boolean;
}

/** A short line that exercises the target-language voice on demand. */
export function sampleLine(language: LanguageCode): string {
  const base = language.split("-")[0]?.toLowerCase() ?? "";
  switch (base) {
    case "pt":
      return "Olá! Esta é a minha voz.";
    case "es":
      return "¡Hola! Esta es mi voz.";
    case "fr":
      return "Bonjour ! Ceci est ma voix.";
    case "de":
      return "Hallo! Das ist meine Stimme.";
    case "it":
      return "Ciao! Questa è la mia voce.";
    case "nl":
      return "Hallo! Dit is mijn stem.";
    case "ar":
      return "مرحباً! هذا صوتي.";
    case "zh":
      return "你好！这是我的声音。";
    case "ru":
      return "Привет! Это мой голос.";
    case "th":
      return "สวัสดี! นี่คือเสียงของฉัน";
    case "hi":
      return "नमस्ते! यह मेरी आवाज़ है।";
    case "ja":
      return "こんにちは！これは私の声です。";
    case "ko":
      return "안녕하세요! 이것은 제 목소리입니다.";
    default:
      return "Hello! This is my voice.";
  }
}

export function PiperVoiceControl(props: Props) {
  const { piper } = props;
  const [testing, setTesting] = useState(false);
  const downloading = piper.status === "downloading";
  const voiceLabel = props.piperVoice.voiceId ? describePiperVoice(props.piperVoice.voiceId) : "no voice yet";

  const onTest = (): void => {
    if (testing) return;
    setTesting(true);
    void props
      .onTestVoice()
      .catch(() => undefined)
      .finally(() => setTesting(false));
  };

  return (
    <div className="stack" style={{ gap: "var(--space-3xs)" }}>
      <div className="row wrap" style={{ gap: "var(--space-2xs)" }}>
        <Segmented
          label="Spoken translations"
          iconsOnMobile
          options={[
            { value: "off", label: "Silent", icon: <IconMute size={13} /> },
            { value: "on", label: "Speak", icon: <IconSpeaker size={13} /> },
          ]}
          value={props.speakOut ? "on" : "off"}
          onChange={(value) => props.onSpeakOutChange(value === "on")}
        />
        <Segmented
          label="Voice gender"
          iconsOnMobile
          options={[
            { value: "female", label: "Female", icon: <IconFemale size={13} /> },
            { value: "male", label: "Male", icon: <IconMale size={13} /> },
          ]}
          value={props.gender}
          onChange={props.onGenderChange}
        />
        <Button size="sm" onClick={onTest} disabled={testing || downloading} title="Speak a sample line in this voice" aria-label="Test this voice">
          <IconPlay size={14} /> <span className="hide-sm">{testing ? "Testing…" : "Test"}</span>
        </Button>
        {props.speaking ? (
          <Button size="sm" iconOnly onClick={props.onStopSpeech} title="Stop speech" aria-label="Stop speech">
            <IconStop size={14} />
          </Button>
        ) : null}
      </div>

      <div className="row wrap" style={{ gap: "var(--space-3xs)", fontSize: "var(--step--2)" }}>
        <span className="dim">
          {languageShortLabel(props.targetLanguage)} · {voiceLabel}
        </span>
        {downloading ? (
          <Badge tone="accent">
            {piper.progress > 0 ? `voice ${Math.round(piper.progress * 100)}%` : "preparing voice…"}
          </Badge>
        ) : null}
        {piper.status === "speaking" ? <Badge tone="good">speaking</Badge> : null}
        {piper.status === "blocked" ? <Badge tone="warn">tap replay to hear</Badge> : null}
        {piper.status === "error" ? <Badge tone="warn">voice failed — fallback used</Badge> : null}
        {!downloading && props.speakOut ? (
          <button type="button" className="linklike" onClick={props.onEnsureVoice} title="Download this voice now so the first line plays instantly and works offline">
            <span className="row" style={{ gap: 4 }}>
              <IconDownload size={12} /> Pre-download voice
            </span>
          </button>
        ) : null}
      </div>

      {props.speakOut && (piper.status === "idle" || piper.status === "ready") && !props.piperVoice.voiceId ? (
        <div className="dim" style={{ fontSize: "var(--step--2)", lineHeight: 1.5 }}>
          First line downloads the voice (~60 MB once, then cached offline). Pre-download it now for instant speech.
        </div>
      ) : null}
      {props.piperVoice.note ? (
        <div className="dim" style={{ fontSize: "var(--step--2)", lineHeight: 1.5 }}>
          {props.piperVoice.note}
        </div>
      ) : null}
      {piper.status === "error" && piper.detail ? (
        <div className="dim" style={{ fontSize: "var(--step--2)", lineHeight: 1.5 }}>
          Voice error: {piper.detail}
        </div>
      ) : null}
      {piper.status === "blocked" && piper.detail ? (
        <div className="dim" style={{ fontSize: "var(--step--2)", lineHeight: 1.5 }}>
          {piper.detail}
        </div>
      ) : null}
    </div>
  );
}
